// Base de conocimiento local del bot de "Tu camino inicial".
//
// El backend real del bot (POST /v1/psi-platform/psychologist/journey/bot, que llamaría a una
// IA con contexto de la etapa) todavía no existe. Mientras tanto, el bot responde con matching
// por palabras clave contra esta base — mismo patrón que ya usa `PsiPlusChatbot.tsx` para el
// programa PSI+.
//
// El contenido de acá está sacado directamente de https://sumate.psimammoliti.com — el portal
// real de onboarding/consulta de Psi Mammoliti (10 categorías, 40+ artículos), no inventado ni
// adaptado del mockup original. Se resumió a formato conversacional para el chat, pero los
// números, plazos y políticas son los reales del sitio.
//
// Cuando el endpoint real exista, `JourneyBot.tsx` lo sigue intentando primero — esto es
// puramente el fallback, no un reemplazo.

export interface JourneyBotContext {
  stage: number;
  isPendingDischarge: boolean;
}

interface KnowledgeEntry {
  triggers: string[];
  answer: (ctx: JourneyBotContext) => string;
}

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

const KNOWLEDGE: KnowledgeEntry[] = [
  {
    triggers: ['hola', 'buenas', 'hey', 'que tal', 'como estas', 'buen dia', 'buenas tardes'],
    answer: (ctx) => ctx.isPendingDischarge
      ? '¡Hola! Estoy para ayudarte con cualquier duda sobre tu situación actual o tu agenda. ¿En qué te puedo orientar?'
      : `¡Hola! Soy tu asistente durante tu período inicial (Etapa ${ctx.stage}). Puedo contarte sobre procesos, agenda, reprogramaciones, pagos, o lo que necesites. ¿Por dónde empezamos?`,
  },
  {
    triggers: ['gracias', 'genial', 'perfecto', 'entendi', 'copado', 'ok', 'dale', '👍'],
    answer: () => '¡Con gusto! Si te surge otra duda, acá estoy.',
  },

  // ── Primeros pasos ──────────────────────────────────────────────────────
  {
    triggers: ['primera sesion', 'encuadre', 'primer encuentro con un paciente', 'como me presento'],
    answer: () =>
      'La primera sesión es para evaluar y encuadrar, no para resolver. Estructura sugerida: presentación profesional, ' +
      'entrevista inicial (motivo de consulta con preguntas abiertas), modalidad de trabajo (tu corriente, duración de ' +
      '50-60 min, frecuencia), contrato terapéutico (política de cancelación), espacio para dudas, y cierre confirmando ' +
      'la próxima sesión. Recabá datos personales, motivo de consulta, antecedentes y una breve evaluación (emociones, ' +
      'discurso, sueño, relaciones). El link de Meet es siempre el mismo — guardalo en tus marcadores.',
  },
  {
    triggers: ['no agendo', 'formulario de no agendamiento', 'que pasa si no agendo', 'despues de una sesion', 'regla de oro'],
    answer: () =>
      'Después de cada sesión solo hay dos caminos: agendar la próxima o completar el formulario de no agendamiento ' +
      '(está en el detalle de cada proceso, "Link Pacientes que no Agendaron"). Regla de oro: intentá agendar DURANTE ' +
      'la sesión, preguntando "¿quedamos mismo día y hora?". Si no hacés ninguna de las dos cosas, a las 24hs recibís ' +
      'un WhatsApp automático preguntando el estado del paciente — si no se regulariza, se carga una falta y puede ' +
      'haber demoras en el cobro de esa sesión.',
  },
  {
    triggers: ['huso horario', 'hora del paciente', 'gmt', 'hora argentina', 'a tus'],
    answer: () =>
      'Siempre hablá en base al horario del paciente (usá la expresión "a tus", ej: "¿te queda bien el jueves a tus 8pm?"). ' +
      'Psi Platform hace la conversión automáticamente — vos pensás en tu hora local, el sistema se encarga del resto. ' +
      'Configurás tu propio huso en el sistema (se detecta automáticamente, lo podés actualizar cuando quieras).',
  },

  // ── Sobre el equipo ─────────────────────────────────────────────────────
  {
    triggers: ['valores del equipo', 'cultura de la empresa', 'en equipo funcionamos mejor'],
    answer: () =>
      'Los tres valores de Psi Mammoliti: "En equipo funcionamos mejor" (compañerismo y cooperación), "Somos ' +
      'responsables de lo que hacemos" (transparencia y rendición de cuentas), y "Evolucionamos constantemente" ' +
      '(proactividad, innovación, perseverancia).',
  },
  {
    triggers: ['areas del equipo', 'equipo interno', 'quien es quien', 'mental health', 'cuantas psicologas'],
    answer: () =>
      'El equipo interno tiene 4 áreas: Mental Health (acompaña tu práctica clínica), Customer Experience/CX (soporte ' +
      'operativo), Administración (documentación, pagos), y Tecnología (Psi Platform y herramientas). Sos parte de una ' +
      'comunidad de más de 100 profesionales de distintos países.',
  },
  {
    triggers: ['beneficios de pertenecer', 'ventajas del equipo', 'supervisiones', 'bono por satisfaccion', 'trabajo remoto'],
    answer: () =>
      'Entre los beneficios: asignación continua de pacientes de todo el mundo, cobro 100% automatizado (no gestionás ' +
      'pagos con el paciente), soporte 24/7 para vos y tus pacientes, trabajo 100% remoto con horarios flexibles, y ' +
      'herramientas de gestión y estadísticas. A medida que crecés: supervisiones individuales mensuales, bonos por ' +
      'satisfacción, flujo constante de pacientes vía PSI+, y espacios de formación.',
  },

  // ── Gestión de Pacientes y Sesiones ──────────────────────────────────────
  {
    triggers: ['asignacion de pacientes', 'cuantos pacientes me asignan', 'etapa inicial', 'proceso de asignacion', 'match'],
    answer: () =>
      'Te asignamos 5 pacientes durante tus primeros 30 días. Esta etapa evalúa tu capacidad de sostener procesos con ' +
      'calidad y continuidad. Según tu desempeño, en los primeros meses el sistema puede asignarte hasta 20 pacientes ' +
      '— no es una garantía. Evaluamos continuidad de tus pacientes, calidad de sesiones y cumplimiento de políticas; ' +
      'si el desempeño no cumple estándares, se pueden limitar asignaciones o proceder a una baja.',
  },
  {
    triggers: ['reprograma seguido', 'nunca vi al paciente', 'seguimiento del paciente', 'consultar estado de un paciente'],
    answer: () =>
      'No hace falta pedirle a CX que contacte al paciente apenas se cancela una sesión — CX ya tiene flujos automáticos ' +
      'con tiempos definidos. Si un paciente reprograma más de 3 veces sin motivo, podés consultarle por mail vos misma, ' +
      'o pedirle a CX que averigüe el motivo. Eso sí: si es un paciente que nunca viste, SIEMPRE debe contactarlo CX, no vos.',
  },
  {
    triggers: ['frecuencia de sesiones', 'quincenal', 'cada 15 dias', 'dos veces por semana'],
    answer: () =>
      'Los procesos se plantean semanalmente por defecto. Podés pasar a frecuencia quincenal sin consultar al equipo si: ' +
      'el paciente lo pide explícitamente (por temas económicos), o el proceso está en etapa avanzada con objetivos ' +
      'mayormente cumplidos. También podés hacer 2 sesiones semanales si lo ves clínicamente apropiado, sin autorización previa.',
  },
  {
    triggers: ['pack de sesiones', 'packs de 4', 'descuento por adelantado'],
    answer: () =>
      'Si el paciente abona 4 sesiones por adelantado, tiene 20% de descuento (lo asume la dirección, no cambia nada en ' +
      'tu liquidación). No es obligatorio mencionarlo, pero sí recomendable si el paciente muestra interés en continuar ' +
      '— derivalo a CX para el link de pago. Por ahora solo para procesos individuales.',
  },
  {
    triggers: ['duracion sesion', 'tolerancia', 'no se conecta', 'puntualidad', 'protocolo de conexion'],
    answer: () =>
      'Cada sesión dura 50-60 minutos. Si el paciente no se conecta en los primeros 0-10 minutos, avisá a CX de inmediato. ' +
      'Tolerancia máxima: 30-35 minutos (si se conecta en ese rango, la sesión se hace por el tiempo restante). Pasados ' +
      'los 35 minutos, la sesión se considera perdida aunque esté abonada. El tiempo de tolerancia aplica solo al ' +
      'paciente — si vos tenés un imprevisto, avisá a CX inmediatamente para reprogramar.',
  },
  {
    triggers: ['alta consensuada', 'dar de alta', 'cerrar un proceso'],
    answer: () =>
      'Cuando los objetivos terapéuticos se cumplieron y ambas partes están conformes, completá el formulario del proceso ' +
      'en tu panel eligiendo "alta consensuada". El paciente NO se elimina de Psi Platform — su cuenta sigue activa por si ' +
      'retoma sesiones más adelante.',
  },
  {
    triggers: ['historia clinica', 'registrar la sesion', 'notas de sesion', 'agregar nota'],
    answer: () =>
      'Se registra desde Psi Platform → Pacientes → Ver paciente → Sesiones → Agregar nota, en texto libre. Registrala el ' +
      'mismo día, antes de cerrar el día — no al día siguiente. Incluí: tema principal tratado, estado emocional ' +
      'observado, intervenciones realizadas, y avances respecto a los objetivos. Las historias clínicas son confidenciales.',
  },
  {
    triggers: ['adhesion al tratamiento', 'objetivos terapeuticos', 'abandono del tratamiento', 'co-construccion de objetivos'],
    answer: () =>
      'La adhesión mejora con: objetivos claros y medibles (formulados en positivo, en conductas observables, con ' +
      'escalas 0-10), una buena reformulación panorámica del caso, foco en soluciones y futuro (pregunta del milagro, ' +
      'excepciones, recursos del paciente), y que vos regules tus propias reacciones emocionales como terapeuta.',
  },

  // ── Gestión de agenda ─────────────────────────────────────────────────────
  {
    triggers: ['disponibilidad requerida', 'cuantas horas necesito', 'horas semanales', 'horas de reprogramacion'],
    answer: () =>
      'Mínimo 14 horas semanales de disponibilidad durante tus primeros 3 meses, más 4 horas adicionales exclusivas para ' +
      'reprogramación (obligatorias, en huecos distintos de días/turnos). Con exactamente 4 horas cumplís la política ' +
      'pero no tenés agenda desbloqueada para nuevos pacientes — necesitás más de 4 para eso. Las horas se cuentan en ' +
      'los próximos 7 días (sin incluir hoy). Regla de las 24hs: un horario de reprogramación libre solo puede tomarse ' +
      'con 24hs de anticipación mínimo.',
  },
  {
    triggers: ['error de disponibilidad', 'horario habilitado por error', 'me agendaron mal'],
    answer: () =>
      'Si un paciente fue asignado en un horario que habías dejado habilitado por error, CX siempre te va a consultar ' +
      'primero. Se pide flexibilidad para reprogramar cuando el error fue nuestro. Tip: tené siempre el calendario ' +
      'abierto al agendar — puede que ya se haya ocupado un horario que viste libre hace horas.',
  },
  {
    triggers: ['reprogramar', 'cancelar sesion', 'link de reprogramacion', 'link de agendamiento', 'sesion gratuita reprogramacion', 'fuerza mayor'],
    answer: () =>
      'El paciente puede reprogramar sin costo hasta 6 horas antes (6:01 vale, 5:59 no). Excepciones válidas dentro de ' +
      'las 6hs: solo fuerza mayor (urgencia de salud propia o de familiar directo, accidentes). "Tráfico" o "se cortó la ' +
      'luz" no son válidas. Si vos necesitás reprogramar con menos de 6hs y no es fuerza mayor, corresponde una sesión ' +
      'gratuita de compensación. El link de reprogramación es único por sesión y vale hasta 6hs antes; el link de ' +
      'agendamiento es fijo por profesional y se usa solo cuando NO hay sesión futura agendada — no los confundas. Si no ' +
      'hay pago 12hs antes, la sesión se cancela automáticamente.',
  },
  {
    triggers: ['paciente del exterior', 'diferencia horaria', 'zona horaria del paciente', 'paciente en otro pais'],
    answer: () =>
      'Siempre coordinamos según la residencia del paciente — nosotros nos adaptamos a su horario. Diferencia horaria ' +
      '(cuántas horas de diferencia con Argentina) y GMT (zona horaria oficial del paciente en el sistema) son conceptos ' +
      'distintos: usá la diferencia para coordinar verbalmente, y asegurate de que el GMT esté bien configurado para que ' +
      'el sistema mande confirmaciones en su hora local. Si el paciente se muda de forma definitiva (+3 meses), avisá a CX.',
  },
  {
    triggers: ['que no hacer', 'agendar fecha pasada', 'mas de una sesion futura', 'malas practicas agendamiento'],
    answer: () =>
      'Tres cosas a evitar: (1) agendar con menos de 6hs de anticipación (recomendado: 24hs) — si no, el sistema no ' +
      'ejecuta la cancelación automática por falta de pago y la sesión puede ocurrir sin estar paga; (2) agendar con ' +
      'fecha pasada; (3) tener más de una sesión futura agendada por paciente — genera confusión con pagos y links.',
  },
  {
    triggers: ['horario fijo', 'cerrar espacio', 'bloquear espacio', 'reservar un horario'],
    answer: () =>
      'No podés agendar más de una sesión futura por paciente, aunque el sistema te lo permita. Para reservarle un ' +
      'horario fijo a un paciente habitual, "cerrás" ese espacio (sale de tu agenda de forma definitiva, nadie más lo ' +
      'ocupa). "Bloquear" es distinto: saca la disponibilidad solo para un día puntual (ej: un feriado o un turno médico), ' +
      'y la semana siguiente el horario vuelve a estar libre.',
  },

  // ── Comunicación ────────────────────────────────────────────────────────
  {
    triggers: ['canales de comunicacion', 'contactar a cx', 'contactar a psicos', 'cuando escribir a cx'],
    answer: () =>
      'CX (WhatsApp +54 9 11 2408-4954, Lun-Vie 7-21 / Sáb 8-20 / Dom 13-19): pacientes que no se conectan, cancelaciones ' +
      'de último momento, problemas técnicos, casos urgentes. Mental Health / psicos@psimammoliti.com (Lun-Vie 9-18): ' +
      'actualizaciones de perfil, asignación de pacientes, bloqueos de agenda, derivaciones, licencias, supervisión. Los ' +
      'bloqueos de agenda van a psicos@, NO a CX.',
  },
  {
    triggers: ['whatsapp con paciente', 'zoom con paciente', 'comunicarme con mi paciente', 'redes sociales paciente'],
    answer: () =>
      'El único medio permitido con el paciente es Google Meet (con tu email @psimammoliti.com) durante la sesión, más ' +
      'mail institucional y, para dudas administrativas, derivarlo al WhatsApp de CX. Enviar un link de otro medio ' +
      'personal (Zoom, WhatsApp, redes) es FALTA GRAVE y motivo de expulsión del equipo. Podés escribirle un mail a un ' +
      'paciente solo después de haber tenido la primera sesión con él.',
  },
  {
    triggers: ['slack', 'comunidad de psicologos', 'como me sumo a slack'],
    answer: () =>
      'Slack es el espacio para conectar con colegas: compartir buenas prácticas, buscar segundas opiniones clínicas, ' +
      'acceder a recursos. No es para temas de políticas o asignaciones (para eso, CX o psicos@). Te llega la invitación ' +
      'a tu mail de Psi Mammoliti — revisá spam, y escribí a psicos@ si no te llegó.',
  },
  {
    triggers: ['psi+', 'psi plus', 'programa de beneficios'],
    answer: () =>
      'PSI+ es el programa que premia el esfuerzo y calidad de trabajo, incentivando tu crecimiento en la clínica. El ' +
      'programa todavía tiene novedades por confirmarse oficialmente — cuando esté más definido debería reflejarse acá. ' +
      'Mientras tanto, cualquier duda puntual sobre tu grupo o recategorización, consultá a psicos@psimammoliti.com.',
  },

  // ── Políticas y procedimientos ────────────────────────────────────────────
  {
    triggers: ['vacaciones', 'dias off', 'licencia', 'como pido vacaciones'],
    answer: () =>
      'Tenés 30 días corridos por año (no hábiles), pedidos desde el módulo de vacaciones en tu panel. Bloqueos previos ' +
      'según cuánto pidas: hasta 9 días sin bloqueo previo, 10-15 días bloqueo 30 días antes, 16-30 días bloqueo 60 días ' +
      'antes, más de 30 días bloqueo 90 días antes + al volver por el doble del tiempo tomado. Si tomás más de 30 días en ' +
      'el año (grupos Inicial/Base/Impulso) o más de 60 (Avanzado/Cumbre), al volver pasás a grupo Inicial. Más de 90 ' +
      'días corridos de una vez no está permitido.',
  },
  {
    triggers: ['feriado', 'feriados argentina', 'atender en feriado'],
    answer: () =>
      'Los feriados argentinos no aplican automáticamente a pacientes de otros países — cada psicóloga decide si atiende. ' +
      'Si decidís no atender, avisá con anticipación y reagendá; tené en cuenta también los feriados del país de tu paciente.',
  },
  {
    triggers: ['que es una falta', 'sancion', 'falta grave', 'falta moderada', 'falta leve'],
    answer: () =>
      'Las faltas se clasifican en 3 niveles. Graves: atender fuera de la clínica, ausentarte sin avisar a CX, contacto ' +
      'por canales no autorizados, quejas serias por destrato. Moderadas: llegar tarde 2+ veces al mes, reprogramaciones ' +
      'sin justificación, no responder a CX, mal uso de la agenda. Leves: quejas menores, desconocer info institucional, ' +
      'sin horarios de reprogramación. Ante una falta: primero comunicación formal por correo; si se repite, se pueden ' +
      'limitar asignaciones o revisar tu continuidad en el equipo.',
  },
  {
    triggers: ['llevarme pacientes', 'pacientes son del equipo', 'contactar pacientes fuera'],
    answer: () =>
      'Los pacientes son del equipo, no de cada profesional individual — no podés llevarlos si dejás Psi Mammoliti, ' +
      'contactarlos por fuera, ni derivarlos a tu práctica privada. Violar esto es FALTA GRAVE y significa ' +
      'desvinculación inmediata, con posibles acciones legales.',
  },
  {
    triggers: ['sesion gratuita', 'responsabilidad solidaria'],
    answer: () =>
      'Si el inconveniente es tuyo (ej. un corte de luz sin respaldo), se reprograma la sesión y luego se agenda una ' +
      'gratuita que asumen tanto vos como CX. Si el error fue de CX/operaciones, se reprograma sin sesión gratuita, pero ' +
      'se pide tu colaboración para reubicar el espacio. Regla simple: la parte que generó el problema se hace cargo — ' +
      'eso es la responsabilidad solidaria.',
  },
  {
    triggers: ['que recibe el paciente', 'pago del paciente', '45 dias sin sesiones', 'cuando se cancela por falta de pago'],
    answer: () =>
      'El paciente paga hasta 12hs antes o la sesión se cancela automáticamente. Puede reprogramar hasta 12hs antes si no ' +
      'pagó, o hasta 6hs antes si ya pagó (sin devolución después). Recibe recordatorios a las 72hs y 24hs antes. Si el ' +
      'paciente encadena 3 reprogramaciones/cancelaciones sin motivo, vos podés dar por finalizado el tratamiento. Si ' +
      'pasan 45 días sin sesiones, el proceso se considera interrumpido y se da de baja del sistema.',
  },
  {
    triggers: ['derivar', 'derivacion', 'como derivo', 'cuando puedo derivar'],
    answer: () =>
      'Solo se derivan temáticas que NO estén en tus áreas registradas — derivar una temática que sí cubrís es falta ' +
      'grave. Ojo: solo 1 de cada 5 pacientes derivados continúa el tratamiento (80% de abandono), así que es la última ' +
      'opción, no la primera. Proceso: (1) evaluá alternativas y supervisá el caso, (2) comunicáselo al paciente EN ' +
      'SESIÓN — nunca por mail — y esperá su confirmación, (3) completá el formulario en Psi Platform (interna o ' +
      'externa), (4) seguí atendiendo hasta la confirmación final por WhatsApp. Sin confirmación del paciente, la ' +
      'derivación se rechaza. El proceso completo toma 1-2 semanas.',
  },
  {
    triggers: ['bloqueo de agenda', 'limite de pacientes', 'cuantos pacientes maximo', 'no quiero mas pacientes'],
    answer: () =>
      'Un bloqueo pausa la asignación de pacientes nuevos, sin afectar a los que ya tenés. Se activa automáticamente si ' +
      'tenés menos de 4 horarios de reprogramación en los próximos 7 días, o si alcanzás tu límite de procesos vigentes ' +
      '(por defecto 40 — podés pedir bajarlo escribiendo a psicos@). También hay un tope de 10 ' +
      'procesos nuevos cada 30 días. Para bloqueos manuales (vacaciones, situación personal), escribí a psicos@psimammoliti.com.',
  },
  {
    triggers: ['recomendar institucion', 'recomendacion externa', 'a donde deriva'],
    answer: () =>
      'Si derivaste externamente y el paciente pide una recomendación, escribí a psicos@ para consultar si hay algo ' +
      'registrado para esa zona. Siempre enviá la recomendación por mail usando el template oficial, con psicos@ en ' +
      'copia — Psi Mammoliti no tiene convenio ni responsabilidad sobre esas instituciones externas.',
  },

  // ── Situaciones especiales ────────────────────────────────────────────────
  {
    triggers: ['urgencia', 'riesgo', 'emergencia con un paciente', 'situacion de riesgo'],
    answer: () =>
      'Ante una urgencia: mantené la calma y conté al paciente en sesión, contactá INMEDIATAMENTE a CX por WhatsApp ' +
      '(+54 9 11 2408-4954) con nombre completo, descripción de la situación, país/ciudad y datos de contacto. No cortes ' +
      'la sesión hasta recibir indicaciones. Mejor avisar de más que quedarte corta ante una emergencia real.',
  },
  {
    triggers: ['darme de baja', 'salir del equipo', 'renunciar', 'dejar psi mammoliti'],
    answer: () =>
      'Escribí a psicos@psimammoliti.com manifestando tu decisión, coordiná con el equipo la transición de tus ' +
      'pacientes y completá las derivaciones necesarias durante el período de transición acordado. Recordá: los ' +
      'pacientes son del equipo, no podés llevarlos ni contactarlos por fuera.',
  },
  {
    triggers: ['me pueden echar', 'desvinculacion', 'causales de baja', 'me pueden dar de baja'],
    answer: () =>
      'Sí — causales incluyen incumplimiento grave de políticas, contactar pacientes por medios no autorizados, intentar ' +
      'llevarte pacientes, faltas reiteradas sin justificación. Las faltas graves (contacto no autorizado, llevarte ' +
      'pacientes) pueden resultar en desvinculación inmediata sin advertencia previa.',
  },

  // ── Consultas frecuentes ───────────────────────────────────────────────────
  {
    triggers: ['modalidad de contratacion', 'facturacion', 'soy independiente'],
    answer: () =>
      'Trabajás como profesional independiente, facturando por servicios profesionales. Necesitás matrícula vigente, ' +
      'seguro de mala praxis actualizado, datos de facturación y datos bancarios para cobrar.',
  },
  {
    triggers: ['documentacion requerida', 'matricula', 'seguro de mala praxis', 'cv actualizado'],
    answer: () =>
      'Documentos requeridos: matrícula profesional vigente, seguro de mala praxis actualizado, datos de facturación ' +
      '(monotributo u otro régimen), datos bancarios, y CV actualizado. Todo se envía por mail a psicos@psimammoliti.com ' +
      '— avisá ahí también cuando algo venza o cambie.',
  },

  // ── Recursos y documentación ────────────────────────────────────────────────
  {
    triggers: ['biblioteca', 'papers', 'material de estudio', 'guias clinicas'],
    answer: () =>
      'La Biblioteca del Equipo tiene papers, artículos, material de formaciones pasadas y guías clínicas — accedé desde ' +
      'el Home de Psicólogos. Si tenés material valioso para compartir, mandalo a psicos@psimammoliti.com.',
  },
  {
    triggers: ['formaciones', 'capacitaciones', 'grabaciones de formaciones'],
    answer: () =>
      'El material de todas las formaciones dictadas (grabaciones, slides, bibliografía) está en la sección de ' +
      'formaciones del Home de Psicólogos. Las próximas se anuncian por mail — estate atenta a tu casilla @psimammoliti.com.',
  },
  {
    triggers: ['consentimiento informado', 'terminos y condiciones paciente'],
    answer: () =>
      'El consentimiento informado ya está integrado en los Términos y Condiciones que el paciente acepta al ' +
      'registrarse — no necesitás gestionar nada adicional. Cubre duración de sesión (50-60 min), medios de contacto, ' +
      'confidencialidad y certificados. Ojo: al ser terapia a distancia, no cubrimos emergencias — el paciente debe ' +
      'recurrir a servicios locales.',
  },

  // ── Liquidaciones y Pagos ───────────────────────────────────────────────────
  {
    triggers: ['liquidacion', 'cuando cobro', 'medios de pago', 'usd', 'usdt', 'pesos argentinos', 'lets bit'],
    answer: () =>
      'Tres modalidades de cobro: transferencia en USD (cuenta radicada en EE.UU., banco o billetera compatible), USDT ' +
      'por red Polygon, o pesos argentinos vía Let\'s Bit (necesitás cuenta ahí, LB Tag y CUIT). El detalle de tu ' +
      'liquidación está en tu Drive antes del día 10 de cada mes; el pago se hace a mes vencido, entre el día 10 y 15 del ' +
      'mes siguiente. La empresa garantiza el monto bruto — comisiones de la entidad receptora corren por tu cuenta. ' +
      'Para gestionar tu modalidad: liquidaciones@psimammoliti.com.',
  },
  {
    triggers: ['orientacion vocacional', ' ov ', 'proceso de orientacion vocacional'],
    answer: () =>
      'La Orientación Vocacional dura 8 sesiones completas, y los honorarios se liquidan solo al finalizar el proceso ' +
      'completo — no se cobra por sesión individual ni por procesos incompletos. Si no podés continuar un proceso en ' +
      'curso, avisá cuanto antes y colaborá con la derivación compartiendo tus notas clínicas.',
  },
];

/**
 * Intenta responder con la base de conocimiento local. Devuelve `null` si no hay ningún match
 * razonable — el llamador decide qué mostrar en ese caso (no fuerza una respuesta genérica acá).
 */
export function answerFromKnowledge(input: string, ctx: JourneyBotContext): string | null {
  const normalized = normalize(input);
  let bestScore = 0;
  let bestEntry: KnowledgeEntry | null = null;

  for (const entry of KNOWLEDGE) {
    let score = 0;
    for (const trigger of entry.triggers) {
      if (normalized.includes(normalize(trigger))) score += trigger.length;
    }
    if (score > bestScore) {
      bestScore = score;
      bestEntry = entry;
    }
  }

  return bestEntry ? bestEntry.answer(ctx) : null;
}
